import { commonServerInstance } from '@/utilities/instances'

export interface DelegationPerson {
  id: number
  name: string
  email: string
}

export type DelegationDisplayStatus = 'active' | 'expired' | 'ended' | 'replaced'

export interface DelegationSummary {
  id: number
  delegator_id: number
  team_id: number
  end_date: string
  status: string
  display_status?: DelegationDisplayStatus
  status_tooltip?: string | null
  ended_reason: string | null
  ended_by?: number | null
  created_at: string | null
  origin_created_at?: string | null
  ended_at?: string | null
  set_up_by?: string
  delegate_user_ids: number[]
  delegates: DelegationPerson[]
}

export interface DelegationCandidate extends DelegationPerson {
  selectable: boolean
  busy_until: string | null
}

export interface DelegationSetup {
  team_name: string
  max_duration_days: number
  latest_end_date: string
  members: DelegationCandidate[]
}

export interface DelegationHistory {
  data: DelegationSummary[]
  total: number
  page: number
  per_page: number
}

export const emptySetup = (): DelegationSetup => ({
  team_name: '',
  max_duration_days: 30,
  latest_end_date: '',
  members: []
})

export const emptyHistory = (): DelegationHistory => ({
  data: [],
  total: 0,
  page: 1,
  per_page: 10
})

export const fetchOwnDelegation = async (
  page = 1,
  perPage = 10
): Promise<{
  delegation: DelegationSummary | null
  setup: DelegationSetup
  history: DelegationHistory
}> => {
  const response = await commonServerInstance.get(
    '/settle/administration/delegation',
    { params: { page, per_page: perPage } }
  )
  const data = response?.data?.data
  return {
    delegation: data?.delegation ?? null,
    setup: {
      team_name: data?.setup?.team_name ?? '',
      max_duration_days: data?.setup?.max_duration_days ?? 30,
      latest_end_date: data?.setup?.latest_end_date ?? '',
      members: data?.setup?.members ?? []
    },
    history: {
      data: data?.history?.data ?? [],
      total: data?.history?.total ?? 0,
      page: data?.history?.page ?? page,
      per_page: data?.history?.per_page ?? perPage
    }
  }
}

/**
 * Whether the caller currently holds delegated AC authority (STLDEV-1557).
 *
 * Display only — the backend authorises every real action on its own, so this
 * should not be used to decide what the user may do. This is only used to 
 * determine whether delegation badges should be displayed.
 */
export const fetchOwnDelegateStatus = async (): Promise<boolean> => {
  const response = await commonServerInstance.get('/settle/delegation/status')
  return response?.data?.data?.is_delegate === true
}

export const createOwnDelegation = async (
  delegateUserIds: number[],
  endDate: string
): Promise<DelegationSummary> => {
  const response = await commonServerInstance.post(
    '/settle/administration/delegation',
    {
      delegate_user_ids: delegateUserIds,
      end_date: endDate
    }
  )
  return response?.data?.data
}

/**
 * Ends a delegation by id. An AC may only end their own; an administrator may
 * end any team's. The backend decides which — the caller just names the row.
 */
export const endDelegation = async (id: number): Promise<void> => {
  await commonServerInstance.post('/settle/administration/delegation/end', { id })
}

export const updateOwnDelegation = async (
  id: number,
  delegateUserIds: number[],
  endDate: string
): Promise<DelegationSummary> => {
  const response = await commonServerInstance.post(
    '/settle/administration/delegation/update',
    {
      id,
      delegate_user_ids: delegateUserIds,
      end_date: endDate
    }
  )
  return response?.data?.data
}
